import {Hemisphere, Kind, Post} from '../types';
import {seasonRange} from './season';

// The To do tab's dates: the "When" presets, a due date said the way a gardener would ("Today",
// "Wed", "3 weeks ago", "Sat 1 Nov"), the tab's sections by season, and the follow-up a tell of
// each kind suggests. Pure; all dates are local "YYYY-MM-DD".

const date = (ymd: string) => new Date(Number(ymd.slice(0, 4)), Number(ymd.slice(5, 7)) - 1, Number(ymd.slice(8, 10)));
const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export function addDays(day: string, days: number): string {
    const d = date(day);
    d.setDate(d.getDate() + days);
    return ymd(d);
}

const daysBetween = (from: string, to: string) => Math.round((date(to).getTime() - date(from).getTime()) / 86_400_000);

export type DuePreset = 'tomorrow' | 'weekend' | 'nextWeek' | 'twoWeeks' | 'nextMonth' | 'season' | 'none';

export const DUE_PRESETS: {preset: DuePreset; label: string}[] = [
    {preset: 'tomorrow', label: 'Tomorrow'},
    {preset: 'weekend', label: 'This weekend'},
    {preset: 'nextWeek', label: 'Next week'},
    {preset: 'twoWeeks', label: 'In 2 weeks'},
    {preset: 'nextMonth', label: 'Next month'},
    {preset: 'season', label: 'This season'},
    {preset: 'none', label: 'Some time'}
];

/** The date a preset means today. "This season" is its last day; "Some time" is no date. */
export function presetDate(preset: DuePreset, today: string, hemisphere: Hemisphere | null | undefined): string | null {
    const dow = date(today).getDay(); // 0 Sunday … 6 Saturday
    switch (preset) {
        case 'tomorrow':
            return addDays(today, 1);
        case 'weekend':
            return dow === 6 || dow === 0 ? today : addDays(today, 6 - dow);
        case 'nextWeek':
            return addDays(today, ((8 - dow) % 7) || 7); // next Monday
        case 'twoWeeks':
            return addDays(today, 14);
        case 'nextMonth': {
            const d = date(today);
            const last = new Date(d.getFullYear(), d.getMonth() + 2, 0).getDate();
            return ymd(new Date(d.getFullYear(), d.getMonth() + 1, Math.min(d.getDate(), last)));
        }
        case 'season':
            return seasonRange(date(today), hemisphere).to;
        case 'none':
            return null;
    }
}

const WEEKDAY = new Intl.DateTimeFormat('en-NZ', {weekday: 'short'});
const DAY_MONTH = new Intl.DateTimeFormat('en-NZ', {weekday: 'short', day: 'numeric', month: 'short'});
const DAY_MONTH_YEAR = new Intl.DateTimeFormat('en-NZ', {day: 'numeric', month: 'short', year: 'numeric'});

/** "Today", "Tomorrow", "Wed", "Sat 1 Nov", "1 Mar 2027", "Yesterday", "3 days ago", "9 weeks ago", "Some time". */
export function dueLabel(due: string | null, today: string): string {
    if (due === null) return 'Some time';
    const n = daysBetween(today, due);
    if (n === 0) return 'Today';
    if (n === 1) return 'Tomorrow';
    if (n === -1) return 'Yesterday';
    if (n < 0) return -n < 14 ? `${-n} days ago` : `${Math.round(-n / 7)} weeks ago`;
    if (n < 7) return WEEKDAY.format(date(due));
    return due.slice(0, 4) === today.slice(0, 4) ? DAY_MONTH.format(date(due)).replace(',', '') : DAY_MONTH_YEAR.format(date(due));
}

export const isOverdue = (todo: Pick<Post, 'dueLocal'>, today: string): boolean => todo.dueLocal !== null && todo.dueLocal < today;

export interface TodoSection {
    key: string;
    label: string;
    todos: Post[];
}

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Overdue, this week, later this season, next season, later, some time; empty ones left out. */
export function todoSections(todos: Post[], today: string, hemisphere: Hemisphere | null | undefined): TodoSection[] {
    const now = seasonRange(date(today), hemisphere);
    const next = seasonRange(date(addDays(now.to, 1)), hemisphere);
    const weekEnd = addDays(today, 6);
    const sections: TodoSection[] = [
        {key: 'overdue', label: 'Overdue', todos: []},
        {key: 'week', label: 'This week', todos: []},
        {key: 'season', label: `Later this ${now.name}`, todos: []},
        {key: 'next', label: capital(next.name), todos: []},
        {key: 'later', label: 'Later', todos: []},
        {key: 'someday', label: 'Some time', todos: []}
    ];
    const at = (key: string) => sections.find(s => s.key === key)!.todos;
    for (const todo of todos) {
        const due = todo.dueLocal;
        if (due === null) at('someday').push(todo);
        else if (due < today) at('overdue').push(todo);
        else if (due <= weekEnd) at('week').push(todo);
        else if (due <= now.to) at('season').push(todo);
        else if (due <= next.to) at('next').push(todo);
        else at('later').push(todo);
    }
    const byDue = (a: Post, b: Post) => (a.dueLocal ?? '').localeCompare(b.dueLocal ?? '') || a.createdAt.localeCompare(b.createdAt);
    return sections.filter(s => s.todos.length > 0).map(s => ({...s, todos: [...s.todos].sort(byDue)}));
}

// ------------------------------------------------------------------ follow-ups

export interface FollowUp {
    /** "Check for seedlings" */
    text: string;
    days: number;
    /** The kind of tell this job becomes when done. */
    tellKind: Kind | null;
}

/**
 * The sensible next check after a tell, first one first. A small hand-made table; tranche 2
 * replaces the days with the gardener's own gaps where their history shows them.
 */
export function followUpsFor(kind: Kind, detail: string | null): FollowUp[] {
    switch (kind) {
        case 'sowed':
            return detail === 'direct'
                ? [
                      {text: 'Check for seedlings', days: 14, tellKind: 'progress'},
                      {text: 'Thin the seedlings', days: 28, tellKind: 'chore'}
                  ]
                : [
                      {text: 'Check for seedlings', days: 10, tellKind: 'progress'},
                      {text: 'Pot on or prick out', days: 28, tellKind: 'potted'}
                  ];
        case 'potted':
            return [
                {text: 'Harden off and plant out', days: 21, tellKind: 'planted'},
                {text: 'Check how they are going', days: 7, tellKind: 'progress'}
            ];
        case 'planted':
            return [
                {text: 'Check for slugs and water in', days: 3, tellKind: 'watered'},
                {text: 'See how it has settled', days: 14, tellKind: 'progress'}
            ];
        case 'problem':
            return [
                {text: 'Check on this', days: 7, tellKind: 'progress'},
                {text: 'Check on this', days: 14, tellKind: 'progress'}
            ];
        case 'fed':
            return detail === 'spray' || detail === 'copper'
                ? [{text: 'Spray again', days: 14, tellKind: 'fed'}]
                : [{text: 'Feed again', days: 28, tellKind: 'fed'}];
        case 'pruned':
            return [{text: 'Check the regrowth', days: detail === 'hard' ? 42 : 21, tellKind: 'progress'}];
        case 'harvest':
            return [{text: 'Pick again', days: 3, tellKind: 'harvest'}];
        case 'watered':
            return [{text: 'Water again', days: 3, tellKind: 'watered'}];
        default:
            return [{text: 'Check on this', days: 7, tellKind: 'progress'}];
    }
}

/** "in 10 days", "in 2 weeks", "in 6 weeks". */
export const inDays = (days: number): string => (days % 7 === 0 && days >= 14 ? `in ${days / 7} weeks` : days === 7 ? 'in a week' : `in ${days} days`);
